import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { Megaphone, Newspaper, CheckCircle, Shield } from 'lucide-react';

const NotificationContext = createContext(null);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

const POLL_INTERVAL = 30000; // 30 seconds
const STORAGE_KEY = 'killarney_last_notification';

export const NotificationProvider = ({ children }) => {
  const [enabled, setEnabled] = useState(true);
  const [lastCheck, setLastCheck] = useState(() => {
    return localStorage.getItem(STORAGE_KEY) || new Date().toISOString();
  });
  const seenNotifications = useRef(new Set());
  const isFirstLoad = useRef(true);

  // Get token from localStorage directly to avoid circular dependency with AuthContext
  const getToken = () => localStorage.getItem('token');

  // Check for user-specific notifications (safety/issue updates)
  const checkUserNotifications = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/my-notifications?unread_only=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const notifications = response.data;
      
      notifications.forEach(notification => {
        if (seenNotifications.current.has(notification.id)) return;
        seenNotifications.current.add(notification.id);
        
        let icon, title;
        
        switch (notification.type) {
          case 'safety_update':
            icon = <Shield className="w-5 h-5 text-blue-500" />;
            title = 'Safety Report Update';
            break;
          case 'issue_update':
            icon = <CheckCircle className="w-5 h-5 text-emerald-500" />;
            title = 'Issue Update';
            break;
          default:
            icon = <Megaphone className="w-5 h-5 text-plp-blue" />;
            title = 'Notification';
        }
        
        toast(title, {
          description: notification.message,
          icon,
          duration: 6000,
          action: notification.type === 'safety_update' ? {
            label: 'View',
            onClick: () => window.location.href = '/dashboard'
          } : notification.type === 'issue_update' ? {
            label: 'View',
            onClick: () => window.location.href = '/fix-it-forward'
          } : undefined
        });
        
        // Mark as read
        axios.patch(`${API}/my-notifications/${notification.id}/read`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => {});
      });
    } catch (error) {
      // Silent fail - user might not be logged in
    }
  }, []);

  const checkNotifications = useCallback(async () => {
    if (!enabled) return;
    
    try {
      const response = await axios.get(`${API}/notifications`, {
        params: { since: lastCheck }
      });
      
      const notifications = response.data;
      
      // Skip showing notifications on first load
      if (isFirstLoad.current) {
        isFirstLoad.current = false;
        // Mark all current notifications as seen
        notifications.forEach(n => seenNotifications.current.add(n.id));
        if (notifications.length > 0) {
          setLastCheck(notifications[0].created_at);
          localStorage.setItem(STORAGE_KEY, notifications[0].created_at);
        }
        return;
      }
      
      // Show new notifications
      notifications.forEach(notification => {
        if (seenNotifications.current.has(notification.id)) return;
        seenNotifications.current.add(notification.id);
        
        let icon, title, description;
        
        switch (notification.type) {
          case 'announcement':
            icon = <Megaphone className="w-5 h-5 text-plp-blue" />;
            title = 'New Announcement';
            description = notification.title;
            break;
          case 'news':
            icon = <Newspaper className="w-5 h-5 text-emerald-500" />;
            title = 'Community News';
            description = notification.title;
            break;
          case 'issue_fixed':
            icon = <CheckCircle className="w-5 h-5 text-green-500" />;
            title = 'Issue Fixed!';
            description = `"${notification.title}" has been resolved`;
            break;
          default:
            return;
        }
        
        toast(title, {
          description,
          icon,
          duration: 5000,
          action: notification.type === 'news' ? {
            label: 'Read',
            onClick: () => window.location.href = '/news'
          } : notification.type === 'issue_fixed' ? {
            label: 'View',
            onClick: () => window.location.href = '/fix-it-forward'
          } : undefined
        });
      });
      
      // Update last check time
      if (notifications.length > 0) {
        const newLastCheck = notifications[0].created_at;
        setLastCheck(newLastCheck);
        localStorage.setItem(STORAGE_KEY, newLastCheck);
      }
    } catch (error) {
      console.error('Failed to check notifications:', error);
    }
  }, [enabled, lastCheck]);

  useEffect(() => {
    // Initial check
    checkNotifications();
    checkUserNotifications();
    
    // Set up polling
    const interval = setInterval(() => {
      checkNotifications();
      checkUserNotifications();
    }, POLL_INTERVAL);
    
    return () => clearInterval(interval);
  }, [checkNotifications, checkUserNotifications]);

  const value = {
    enabled,
    setEnabled,
    checkNotifications
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;
